'use client';

import { styled } from 'styled-components';
import type { IconNameType } from '@wistia/ui';
import { Icon } from '@wistia/ui';
import cs from 'classnames';
import * as Styled from '@lib/styles';

type FeatureCardProps = {
  /**
   * Paragraph text of the card
   */
  description: string;
  /**
   * A client-side path or URL to link to
   */
  href: string;
  /**
   * The name of the icon to be used
   */
  icon?: IconNameType;
  /**
   * Which variant of the FeatureCard to use: small, standard (default), large
   */
  size?: 'lg' | 'sd' | 'sm';
  /**
   * Heading text of the card headline
   */
  headline: string;
};

const CardContainer = styled.a`
  background-color: ${({ theme }) => theme.colors.blue100};
  border-radius: ${({ theme }) => theme.radius.sm};
  box-shadow: 0 0.063em 0.063em 0 ${({ theme }) => theme.colors.dropShadowBlue};
  color: ${({ theme }) => theme.colors.blue800};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing['0.5']};
  padding: ${({ theme }) => `${theme.spacing['1.25']} ${theme.spacing['1']}`};
  transition: transform 0.25s ease-out;

  &:visited {
    color: ${({ theme }) => theme.colors.blue800};
  }

  &:hover {
    box-shadow: 0 0.25em 1.5em 0 ${({ theme }) => theme.colors.dropShadowBlue};
    color: ${({ theme }) => theme.colors.blue800};
    transform: translateY(-0.25rem);
  }

  &:active {
    box-shadow: 0 0.125em 0.625em 0
      ${({ theme }) => theme.colors.dropShadowBlue};
  }

  &.sd {
    ${({ theme }) => theme.mq.minWidth.medium} {
      flex-direction: row;
      gap: ${({ theme }) => theme.spacing['2']};
    }
  }

  &.sm {
    padding: ${({ theme }) => theme.spacing['1']};
    height: 100%;

    ${({ theme }) => theme.mq.minWidth.medium} {
      padding: ${({ theme }) =>
        `${theme.spacing['1.25']} ${theme.spacing['1']}`};
    }
  }

  &.lg {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing['1']};

    ${({ theme }) => theme.mq.minWidth.medium} {
      padding: ${({ theme }) =>
        `${theme.spacing['2']} ${theme.spacing['1.5']}`};
    }
  }
`;

const CardTitle = styled.h4`
  align-items: center;
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  margin: 0;

  ${({ theme }) => theme.mq.minWidth.medium} {
    /* Add a gap of space between CardTitle & CardIcon on medium screens, right before mobile breakpoint. */
    gap: ${({ theme }) => theme.spacing['0.5']};
  }

  ${CardContainer}.sd & {
    font-size: ${({ theme }) => theme.typography.fontSize.headingFour};
    ${({ theme }) => theme.mq.minWidth.medium} {
      font-size: ${({ theme }) =>
        theme.typography.fontSize.desktop.headingFour};
    }
  }

  ${CardContainer}.sm & {
    font-size: ${({ theme }) => theme.typography.fontSize.headingFive};
    ${({ theme }) => theme.mq.minWidth.medium} {
      font-size: ${({ theme }) =>
        theme.typography.fontSize.desktop.headingFive};
    }
  }

  ${CardContainer}.lg & {
    font-size: ${({ theme }) => theme.typography.fontSize.headingThree};

    ${({ theme }) => theme.mq.minWidth.medium} {
      font-size: ${({ theme }) =>
        theme.typography.fontSize.desktop.headingThree};
    }
  }
`;

const CardDescription = styled.p`
  flex-basis: 100%;
  font-weight: ${({ theme }) => theme.typography.fontWeight.body};
  margin: 0;
`;

const CardIcon = styled(Icon)`
  flex-shrink: 0;
  height: 1.75rem;
  width: 1.75rem;

  ${CardContainer}.sm & {
    height: 1rem;
    width: 1rem;

    ${({ theme }) => theme.mq.minWidth.medium} {
      height: 1.5rem;
      width: 1.5rem;
    }
  }

  ${CardContainer}:hover & {
    color: ${({ theme }) => theme.colors.blue};
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing['0.5']};
  justify-content: space-between;
  width: 100%;

  ${CardContainer}.lg & {
    gap: ${({ theme }) => theme.spacing['0.75']};
  }
`;

const CardLinkIcon = styled(CardIcon)`
  ${({ theme }) => theme.mq.minWidth.medium} {
    opacity: 0;

    ${CardContainer}:hover & {
      opacity: 1;
      transition: opacity 0.25s ease-in-out;
    }
  }
`;

const FeatureCard = ({
  description,
  href,
  icon,
  size = 'sd',
  headline,
}: FeatureCardProps) => {
  const CardDescriptionFontSize = {
    sd: Styled.BodySm,
    sm: Styled.BodyXs,
    lg: Styled.BodyLg,
  };

  return (
    <CardContainer className={cs(size)} href={href}>
      {icon ? <CardIcon type={icon} /> : null}
      <CardHeader>
        <CardTitle as="h3">
          {headline} <CardLinkIcon role="presentation" type="arrow-right" />
        </CardTitle>
        <CardDescription as={CardDescriptionFontSize[size]}>
          {description}
        </CardDescription>
      </CardHeader>
    </CardContainer>
  );
};

export default FeatureCard;
